/**
 * LABELS — mapping des IDs techniques vers libellés affichables
 *
 * Single source of truth pour toutes les pages qui affichent
 * level_id, goal_id, place, etc.
 */

// ============================================
// NIVEAUX
// ============================================

export const LEVEL_LABELS: Record<string, string> = {
  beginner: "Débutant",
  intermediate: "Intermédiaire",
  advanced: "Avancé",
}

export function getLevelLabel(levelId: string | undefined | null): string {
  if (!levelId) return "—"
  return LEVEL_LABELS[levelId] ?? levelId
}

// ============================================
// OBJECTIFS
// ============================================

export const GOAL_LABELS: Record<string, string> = {
  // Beginner
  "perte-de-poids": "Perte de poids",
  "prise-de-muscle": "Prise de muscle",
  "remise-en-forme": "Remise en forme",
  // Intermediate
  "seche-definition": "Sèche / Définition",
  "construction-musculaire": "Construction musculaire",
  "force-endurance": "Force / Endurance",
  // Advanced
  "decoupe-extreme": "Découpe extrême",
  "hypertrophie": "Hypertrophie",
  "force-athletique": "Force athlétique",
}

export function getGoalLabel(goalId: string | undefined | null): string {
  if (!goalId) return "—"
  return GOAL_LABELS[goalId] ?? goalId
}

// ============================================
// LIEU D'ENTRAÎNEMENT
// ============================================

export const PLACE_LABELS: Record<string, string> = {
  gym: "Salle de sport",
  home: "À la maison",
}

export function getPlaceLabel(place: string | undefined | null): string {
  if (!place) return "—"
  return PLACE_LABELS[place] ?? place
}

// ============================================
// GENRE
// ============================================

export const GENDER_LABELS: Record<string, string> = {
  male: "Homme",
  female: "Femme",
}

export function getGenderLabel(gender: string | undefined | null): string {
  if (!gender) return "—"
  return GENDER_LABELS[gender] ?? gender
}

// ============================================
// OFFRE
// ============================================

export const OFFER_LABELS: Record<string, string> = {
  "ebook": "E-book Personnalisé",
  "coaching-evolutif": "Coaching Évolutif",
  "coaching-vip": "Coaching VIP",
}

export function getOfferLabel(offerId: string | undefined | null): string {
  if (!offerId) return "—"
  return OFFER_LABELS[offerId] ?? offerId
}

// ============================================
// PROGRAMME (combinaison level + goal)
// ============================================

/**
 * Génère le nom du programme affiché, ex : "Intermédiaire — Sèche"
 */
export function getProgramName(levelId?: string | null, goalId?: string | null): string {
  const level = getLevelLabel(levelId)
  const goalShort = getGoalShortLabel(goalId)
  return `${level} — ${goalShort}`
}

/**
 * Version courte des objectifs pour le nom du programme
 */
const GOAL_SHORT_LABELS: Record<string, string> = {
  "perte-de-poids": "Perte de poids",
  "prise-de-muscle": "Prise de muscle",
  "remise-en-forme": "Remise en forme",
  "seche-definition": "Sèche",
  "construction-musculaire": "Construction",
  "force-endurance": "Force / Endurance",
  "decoupe-extreme": "Découpe",
  "hypertrophie": "Hypertrophie",
  "force-athletique": "Force athlétique",
}

export function getGoalShortLabel(goalId: string | undefined | null): string {
  if (!goalId) return "—"
  return GOAL_SHORT_LABELS[goalId] ?? goalId
}

// ============================================
// HELPERS DATE
// ============================================

/**
 * Formate une date ISO (YYYY-MM-DD) en format FR (DD/MM/YYYY)
 */
export function formatDateFR(iso: string | null | undefined): string {
  if (!iso) return "—"
  try {
    const [y, m, d] = iso.split("T")[0].split("-")
    return `${d}/${m}/${y}`
  } catch {
    return "—"
  }
}

/**
 * Calcule la semaine actuelle du programme à partir de la date de début.
 * Retourne entre 1 et 12.
 */
export function getProgramWeek(
  startDateIso: string | null | undefined,
  totalWeeks: number = 12
): number {
  if (!startDateIso) return 1
  try {
    const start = new Date(startDateIso)
    const now = new Date()
    const diffMs = now.getTime() - start.getTime()
    const diffWeeks = Math.floor(diffMs / (7 * 24 * 60 * 60 * 1000))
    return Math.max(1, Math.min(totalWeeks, diffWeeks + 1))
  } catch {
    return 1
  }
}

/**
 * Formate "Membre depuis" depuis une date ISO
 */
export function formatMemberSince(iso: string | null | undefined): string {
  if (!iso) return "—"
  try {
    const d = new Date(iso)
    return d.toLocaleDateString("fr-FR", { month: "long", year: "numeric" })
      .replace(/^./, (s) => s.toUpperCase())
  } catch {
    return "—"
  }
}

// ============================================
// OBJECTIFS DISPONIBLES PAR NIVEAU
// ============================================

/**
 * Objectifs proposés pour chaque niveau. Reprend la liste du wizard (StepGoal) sans
 * ses illustrations : l'écran Mon programme doit proposer exactement les mêmes choix,
 * puisque le couple (niveau, objectif) désigne un programme du catalogue et qu'une
 * combinaison hors de cette table n'aurait aucun programme derrière.
 */
export const GOAL_IDS_BY_LEVEL: Record<string, string[]> = {
  beginner: ["perte-de-poids", "prise-de-muscle", "remise-en-forme"],
  intermediate: ["seche-definition", "construction-musculaire", "force-endurance"],
  advanced: ["decoupe-extreme", "hypertrophie", "force-athletique"],
}

export function getGoalsForLevel(levelId: string | undefined | null): string[] {
  if (!levelId) return GOAL_IDS_BY_LEVEL.beginner
  return GOAL_IDS_BY_LEVEL[levelId] ?? GOAL_IDS_BY_LEVEL.beginner
}

// ============================================
// JOURS D'ENTRAÎNEMENT
// ============================================

/** Codes stockés dans wizard_submissions.training_days, dans l'ordre de la semaine. */
export const TRAINING_DAYS: { id: string; short: string; long: string }[] = [
  { id: "mon", short: "Lun", long: "Lundi" },
  { id: "tue", short: "Mar", long: "Mardi" },
  { id: "wed", short: "Mer", long: "Mercredi" },
  { id: "thu", short: "Jeu", long: "Jeudi" },
  { id: "fri", short: "Ven", long: "Vendredi" },
  { id: "sat", short: "Sam", long: "Samedi" },
  { id: "sun", short: "Dim", long: "Dimanche" },
]

/**
 * Les jours où les séances sont RÉELLEMENT placées : `check-and-populate-next-block`
 * (ALLOWED_JS_DAYS) pose chaque vague de 4 séances sur lundi, mardi, jeudi, vendredi.
 * `training_days` n'est lu par aucune fonction : l'afficher ferait croire à un réglage
 * qui ne fait rien. Chantier séparé pour que le placement le lise (lot 3).
 */
export const PROGRAM_TRAINING_DAYS_LABEL = "Lun · Mar · Jeu · Ven"

export function formatTrainingDays(days: string[] | null | undefined): string {
  if (!days || days.length === 0) return "Non défini"
  const order = TRAINING_DAYS.map((d) => d.id)
  return [...days]
    .sort((a, b) => order.indexOf(a) - order.indexOf(b))
    .map((id) => TRAINING_DAYS.find((d) => d.id === id)?.short ?? id)
    .join(" · ")
}

// ============================================
// FORMULE (subscriptions.plan) — remplace l'« offre » du wizard depuis le lot 3
// ============================================

export const PLAN_LABELS: Record<string, string> = {
  autonomie: "Autonomie",
  coaching: "Coaching",
  premium: "Premium",
  lyon: "Zeyone Lyon",
}

export function getPlanLabel(plan: string | undefined | null): string {
  if (!plan) return "—"
  return PLAN_LABELS[plan] ?? plan
}

// ============================================
// BLESSURES DÉCLARÉES (athlete_injuries, lot 3)
// ============================================

/** Zones proposées au diagnostic, dans l'ordre d'affichage. Clés = contrainte CHECK en base. */
export const INJURY_ZONES: { id: string; label: string }[] = [
  { id: "epaule", label: "Épaule" },
  { id: "coude", label: "Coude" },
  { id: "poignet", label: "Poignet / main" },
  { id: "cou", label: "Cou" },
  { id: "haut_dos", label: "Haut du dos" },
  { id: "bas_dos", label: "Bas du dos" },
  { id: "hanche", label: "Hanche" },
  { id: "genou", label: "Genou" },
  { id: "cheville", label: "Cheville / pied" },
  { id: "autre", label: "Autre" },
]

/** Le critère est la douleur, pas l'ancienneté (décision du lot 3). */
export const INJURY_STATUS_LABELS: Record<string, string> = {
  guerie: "Guérie, plus aucune douleur",
  genante: "Elle me fait encore mal ou me gêne",
}

/** « Genou », ou pour la zone libre le texte du client (« Mâchoire »), à défaut « Autre ». */
export function getInjuryZoneLabel(zone: string, zoneOther?: string | null): string {
  if (zone === "autre") return zoneOther?.trim() || "Autre"
  return INJURY_ZONES.find((z) => z.id === zone)?.label ?? zone
}
